<template>
  <div>
    <!-- 回复区主容器 -->
    <div class="reply-section">
      <!-- 顶部导航栏 -->
      <div class="reply-navbar" v-if="!isReplyDetail">
        <div class="reply-tabs">
          <!-- 全部回复标签 -->
          <span
            class="tab"
            :class="{ active: replyTab === ReplyTab.All }"
            @click="threadStore.switchReplyTab(ReplyTab.All)"
            >{{ t("postDetail.allReplies") }}</span
          >
          <!-- 只看作者标签 -->
          <span
            class="tab"
            :class="{ active: replyTab === ReplyTab.OnlyAuthor }"
            @click="threadStore.switchReplyTab(ReplyTab.OnlyAuthor)"
            >{{ t("postDetail.onlyAuthor") }}</span
          >
        </div>
        <!-- 回复详情页面不显示排序 -->
        <div class="reply-sort">
          <!-- 最早排序 -->
          <span
            class="sort"
            :class="{ active: replySort === ReplySort.Earliest }"
            @click="threadStore.switchReplySort(ReplySort.Earliest)"
            >{{ t("postDetail.earliest") }}</span
          >
          <span class="divider">|</span>
          <!-- 最新排序 -->
          <span
            class="sort"
            :class="{ active: replySort === ReplySort.Latest }"
            @click="threadStore.switchReplySort(ReplySort.Latest)"
            >{{ t("postDetail.latest") }}</span
          >
        </div>
      </div>
      <!-- 回复列表渲染区 -->
      <div v-if="currentReplies.length" class="reply-list">
        <div
          v-for="(reply, floorIndex) in currentReplies"
          :key="reply.id"
          class="reply-card"
          :id="`reply-${reply.id}`"
          :class="{ 'reply-highlight': reply.id === highlightReplyId }"
        >
          <!-- 回复头部：头像、用户名、楼层 -->
          <div class="reply-header" @click="handleReplyComment(reply)">
            <Avatar
              :avatar-index="reply.avatarIndex"
              size="medium"
              class="reply-avatar"
              :clickable="true"
              :enable-navigate="true"
              :user-id="reply.authorid"
              :avatar-frame="reply.avatar_frame"
            />
            <div class="reply-user-info">
              <div class="reply-username-wrapper">
                <span class="reply-username">{{ reply.username }}</span>
                <!-- 用户称号展示 -->
                <div
                  v-if="reply.user_title && reply.user_title.length > 0"
                  class="reply-titles"
                >
                  <span
                    v-for="(title, index) in reply.user_title"
                    :key="index"
                    class="reply-title-badge"
                  >
                    {{ title }}
                  </span>
                </div>
              </div>
              <span class="reply-floor">{{
                isReplyDetail
                  ? `${t("postDetail.replyTo")} ${reply.replyTo}`
                  : `${floorIndex + 1}F`
              }}</span>
            </div>
            <div class="reply-actions">
              <span
                v-if="
                  !languageStore.isTranslated(reply.id) &&
                  languageStore.curLanguage !== 'all' &&
                  languageStore.languageList.length > 1
                "
                class="translate-btn"
                @click.stop="handleReplyTranslate(reply)"
                :title="t('postDetail.translate')"
              >
                {{ t("postDetail.translate") }}
              </span>
              <img
                src="@/assets/more.png"
                alt="more"
                class="more-icon"
                @click.stop="handleReplyMore(reply)"
              />
            </div>
          </div>
          <!-- 回复内容 -->
          <div class="reply-content">
            <div
              v-if="!languageStore.isTranslated(reply.id)"
              v-html="decodeHtml(reply.content)"
              @click="handleReplyImageClick($event, reply)"
            ></div>
            <div v-else>
              <div class="translated-tip">
                {{ t("postDetail.translatedTo") }}
                {{ languageStore.currentLanguageName }}
              </div>
              <div
                v-html="
                  decodeHtml(languageStore.getTranslatedContent(reply.id) || '')
                "
                @click="handleReplyImageClick($event, reply)"
              ></div>
            </div>
          </div>
          <!-- 引用内容（最多显示3条，超出可查看更多） -->
          <div
            v-if="reply.child_replies.length"
            class="reply-quotes"
            @click.stop="navigateToQuote(reply)"
          >
            <div
              v-for="(quote, idx) in reply.child_replies.slice(0, 3)"
              :key="idx"
              class="reply-quote"
            >
              <span
                class="quote-username"
                @click.stop="handleUserClick(quote.userId)"
                >{{ quote.username }}</span
              >
              <span v-if="quote.isAuthor" class="author">{{
                t("postDetail.author")
              }}</span>
              <template v-if="quote.replyTo">
                <span class="quote-reply">{{ t("postDetail.replyTo") }}</span>
                <span
                  class="quote-username"
                  @click.stop="handleUserClick(quote.replyToId)"
                  >{{ quote.replyTo }}</span
                >
                <span v-if="quote.isTargetAuthor" class="author">{{
                  t("postDetail.author")
                }}</span>
              </template>
              <span class="quote-colon">: </span>
              <span
                class="quote-content"
                v-html="decodeHtml(quote.content)"
              ></span>
            </div>
            <!-- 超过3条引用时，显示查看更多提示 -->
            <div v-if="reply.child_replies.length > 3" class="view-more-quotes">
              {{
                t("postDetail.allComments", { num: reply.child_replies.length })
              }}
              →
            </div>
          </div>
          <!-- 回复底部：时间、操作（评论、点赞） -->
          <div class="reply-footer">
            <span class="reply-date">{{ reply.date }}</span>
            <div class="reply-actions">
              <img
                src="@/assets/comment.png"
                class="icon"
                @click="handleReplyComment(reply)"
              />
              <div class="like-item" @click="handleReplyLike(reply)">
                <div
                  class="icon like-icon"
                  :class="{ 'is-active': reply.isLiked }"
                ></div>
                <span>{{ reply.likes }}</span>
              </div>
            </div>
          </div>
        </div>
        <!-- 分页加载更多提示/按钮 -->
        <div ref="loadMoreRef"></div>
        <div
          v-if="currentPagination?.has_next || loading"
          class="load-more-loading"
          @click="loadMore"
        >
          <span v-if="loading">{{ t("common.loadingMore") }}</span>
          <span v-else>{{ t("common.loadMore") }}</span>
        </div>
        <div
          v-else-if="currentReplies.length && !currentPagination?.has_next"
          class="load-more-end"
        >
          <span>{{ t("common.noMore") }}</span>
        </div>
      </div>
      <!-- 无回复时的占位提示 -->
      <Empty v-else :tip="t('postDetail.noContent')" style="margin-top: 20px" />
    </div>
    <!-- 在模板底部添加ShareModal弹窗 -->
    <OperateModal
      v-model="showOperate"
      :shareLink="currentShareLink"
      :show-delete="currentShareReply?.authorid == userStore.userInfo?.uid"
      :reply-id="currentShareReply?.id"
      @delete="handleDelete"
    />
    <CommentModal
      v-model="showComment"
      :reply-to="replyTo"
      @submit="handleCommentSubmit"
    />
  </div>
</template>

<script setup lang="ts">
// 引入vue相关API
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import OperateModal from "@/components/modal/OperateModal.vue";
import Avatar from "@/components/common/Avatar.vue";
import { decodeHtml, extractImageLinks, getDeviceLanguage } from "@/utils";
import { ContentEventDelegation } from "@/utils/contentEventDelegation";
// 引入pinia
import { storeToRefs } from "pinia";
import { useThreadStore, ReplyTab, ReplySort } from "@/stores/thread";
import {
  useToastStore,
  useUserStore,
  useLikeStore,
  useImageViewerStore,
  useLanguageStore,
} from "@/stores";
import { getUrlParams } from "@/utils";
import { clickButton, pageType } from "@/utils/point";
import CommentModal from "@/components/postDetail/CommentModal.vue";
import Empty from "@/components/common/Empty.vue";

// 引用内容结构
interface Quote {
  username: string; // 被引用用户名
  replyTo?: string; // 回复对象用户名
  isAuthor?: boolean; // 是否为作者
  userId: number; // 被引用用户ID
  replyToId: number; // 回复对象用户ID
  content: string; // 引用内容
}

// 回复结构
interface Reply {
  id: number; // 唯一ID
  avatarIndex: number; // 头像索引
  username: string; // 用户名
  floor: string; // 楼层
  content: string; // 回复内容
  child_replies: Quote[]; // 引用内容
  date: string; // 回复日期
  likes: number; // 点赞数
  isLiked: boolean; // 是否已点赞
}

// 组件props定义
interface Props {
  authorUsername?: string; // 作者用户名
  initialTab?: ReplyTab; // 初始标签
  initialSort?: ReplySort; // 初始排序
  isReplyDetail?: boolean; // 是否为回复详情页面
}

// 获取props和emit，设置默认值
const props = withDefaults(defineProps<Omit<Props, "replies">>(), {
  authorUsername: "",
  initialTab: ReplyTab.All,
  initialSort: ReplySort.Earliest,
  isReplyDetail: false,
});

// 国际化方法
const { t, locale } = useI18n();
// 路由实例
const router = useRouter();
const route = useRoute();
const toast = useToastStore();
const userStore = useUserStore();
const likeStore = useLikeStore();
const imageViewer = useImageViewerStore();
const threadStore = useThreadStore();
const {
  replyList,
  replyDetailList,
  pagination,
  replyDetailPagination,
  loading,
  formattedReplyList,
  replyTab,
  replySort,
} = storeToRefs(threadStore);

// 获取当前帖子ID（用于监听帖子切换）
const currentThreadId = computed(() => threadStore.currentThreadId);

const showComment = ref(false);
const replyTo = ref("");
const replyPid = ref<number | null>(null);
const languageStore = useLanguageStore();
const handleCommentSubmit = async (content: string) => {
  const tid = threadStore.currentThreadId;
  if (!tid) return;
  if (replyPid.value) {
    try {
      // 调用帖子回复的回复
      toast.showToast(t("postDetail.sending"), "loading", 10000);
      const res = await threadStore.replyToComment(
        tid,
        content,
        replyPid.value,
        getDeviceLanguage()
      );
      if (res.code == 200) {
        toast.showToast(t("postDetail.sendSuccess"), "success", 1000);
        showComment.value = false;
      } else {
        toast.showErrorCodeToast(res.code, t);
      }
    } catch (error) {
      toast.showToast(t("postDetail.sendFailed"), "error", 1000);
    }
  }
};
const handleDelete = async () => {
  // 删除后刷新帖子详情
  if (!threadStore.currentThreadId) return;
  await threadStore.getThreadDetail(threadStore.currentThreadId);
  showOperate.value = false;
};
const handleReplyComment = (reply: Reply) => {
  if (!userStore.checkLogin()) {
    return;
  }
  showComment.value = true;
  replyTo.value = reply.username;
  replyPid.value = reply.id;
};
// 加载更多逻辑，调用 threadStore 的 loadMoreReplies
const loadMoreRef = ref<HTMLElement | null>(null);
const observer = ref<IntersectionObserver | null>(null);

const loadMore = () => {
  if (!pagination.value?.has_next || loading.value) return;
  const tid = threadStore.currentThreadId;
  if (!tid) return;
  threadStore.loadMoreReplies(tid);
};

const initIntersectionObserver = () => {
  if (observer.value) observer.value.disconnect();
  observer.value = new window.IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        loadMore();
      }
    },
    { root: null, rootMargin: "100px", threshold: 0.1 }
  );
  if (loadMoreRef.value) observer.value.observe(loadMoreRef.value);
};

// 根据 isReplyDetail 属性选择数据源
const currentReplies = computed(() => {
  if (props.isReplyDetail) {
    // 回复详情页面使用 replyDetailList
    return replyDetailList.value.map((item: any) => ({
      id: Number(item.pid || item.id),
      avatarIndex: item.author_info?.avatar || item.avatarIndex || 1,
      username: item.author_info?.username || item.author || "",
      authorid: item.author_info?.uid || item.authorid || 0,
      floor: "",
      message: item.message || "",
      content: item.content || "",
      isAuthor: item.author_info?.username === props.authorUsername,
      isTargetAuthor: item.target_user_info?.username === props.authorUsername,
      child_replies: [],
      replyTo: item.replied_user_info?.username || "",
      date: item.dateline_formatted || item.create_time || "",
      likes: item.likes,
      isLiked: item.is_liked,
      avatar_frame: item.author_info?.avatar_frame || "",
      user_title: item.author_info?.user_title || [],
    }));
  } else {
    // 普通帖子详情页面使用 formattedReplyList
    return formattedReplyList.value;
  }
});

// 根据 isReplyDetail 属性选择分页信息
const currentPagination = computed(() => {
  return props.isReplyDetail ? replyDetailPagination.value : pagination.value;
});

// 初始化回复列表相关的观察器和事件绑定（公共逻辑）
const initReplyListSetup = () => {
  nextTick(() => {
    initIntersectionObserver();
    // 统一设置回复内容相关的事件绑定
    contentEventDelegation.init();
  });
};

// 监听帖子ID变化（切换不同帖子时重新渲染）
watch(
  currentThreadId,
  (newId, oldId) => {
    // 只在真正切换帖子时执行（避免初始化时重复执行）
    if (newId && newId !== oldId) {
      initReplyListSetup();
    }
  },
  { flush: "post" }
);

// 点赞/取消点赞
const handleReplyLike = (reply: Reply) => {
  if (reply.isLiked) {
    likeStore.cancelLike(reply.id, "post");
    threadStore.updateReplyLikeStatus(reply.id, false, props.isReplyDetail);
    toast.showToast(t("profile.cancelLikeSuccess"), "success");
  } else {
    likeStore.addLike(reply.id, "post");
    threadStore.updateReplyLikeStatus(reply.id, true, props.isReplyDetail);
    toast.showToast(t("profile.addLikeSuccess"), "success");
  }
};

// ShareModal 相关状态
const showOperate = ref(false);
const currentShareReply = ref<Reply | null>(null);
const currentShareLink = ref<string>("");

// 新增：处理每条回复的翻译按钮点击
const handleReplyTranslate = async (reply: Reply) => {
  toast.showToast(t("postDetail.translating"), "loading", 10000);
  const targetLanguage = languageStore.curLanguage;
  const response = await languageStore.translate(
    reply.id,
    reply.content,
    targetLanguage
  );

  if (response && response.code === 200) {
    toast.showToast(t("postDetail.translateSuccess"), "success", 1000);
  } else {
    toast.showToast(t("postDetail.translateFailed"), "error", 1000);
  }
};

// 新增：处理每条回复的更多按钮点击
const handleReplyMore = (reply: Reply) => {
  currentShareReply.value = reply;
  // 生成带楼层id的分享链接，作为search参数
  const { origin, pathname, search, hash } = window.location;
  const urlParams = new URLSearchParams(search);
  urlParams.set("replyFloor", reply.id.toString());

  currentShareLink.value = `${origin}${pathname}?${urlParams.toString()}${hash}`;
  showOperate.value = true;
};

// 高亮的楼层id
const highlightReplyId = ref<number | null>(null);

// 使用事件委托，减少 DOM 操作和事件绑定
// 在回复列表容器上统一处理点击事件，避免频繁绑定/解绑
const contentEventDelegation = new ContentEventDelegation(
  ".reply-list",
  router
);

// 点击用户名跳转用户页（用于模板中的直接绑定）
const handleUserClick = (userId: string) => {
  router.push(`/user/${userId}`);
};

onMounted(() => {
  // 检查search参数中的replyFloor
  const replyFloor = getUrlParams("replyFloor");
  if (replyFloor) {
    highlightReplyId.value = Number(replyFloor);
  }

  // 等待所有图片加载完成后再滚动
  const scrollToHighlightedReply = () => {
    if (highlightReplyId.value) {
      const el = document.getElementById(`reply-${highlightReplyId.value}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  // 如果有高亮的回复，等待图片加载
  if (highlightReplyId.value) {
    // 获取页面中所有图片
    const images = document.querySelectorAll("img");
    let loadedImages = 0;
    const totalImages = images.length;

    if (totalImages === 0) {
      // 如果没有图片，直接滚动
      scrollToHighlightedReply();
    } else {
      // 监听所有图片的加载完成事件
      images.forEach((img) => {
        if (img.complete) {
          loadedImages++;
          if (loadedImages === totalImages) {
            // 所有图片都已加载完成
            setTimeout(scrollToHighlightedReply, 100);
          }
        } else {
          img.onload = () => {
            loadedImages++;
            if (loadedImages === totalImages) {
              // 所有图片都已加载完成
              setTimeout(scrollToHighlightedReply, 100);
            }
          };
          img.onerror = () => {
            loadedImages++;
            if (loadedImages === totalImages) {
              // 所有图片都已加载完成（包括加载失败的）
              setTimeout(scrollToHighlightedReply, 100);
            }
          };
        }
      });
    }
  }
  // 初始化回复列表相关的观察器和事件绑定
  initReplyListSetup();
});

// 组件卸载时清理事件监听器
onUnmounted(() => {
  contentEventDelegation.cleanup();
  if (observer.value) {
    observer.value.disconnect();
  }
});

// 图片预览相关
const handleReplyImageClick = (event: Event, reply: Reply) => {
  const target = event.target as HTMLImageElement;
  if (target.tagName === "IMG") {
    const content = reply.content || "";
    const imageLinks = extractImageLinks(content);

    if (imageLinks.length > 0) {
      // 找到被点击的图片在数组中的索引
      const clickedSrc = target.src;
      const imageIndex = imageLinks.findIndex((link) => link === clickedSrc);

      if (imageIndex !== -1) {
        imageViewer.show(imageLinks, imageIndex);
      }
    }
  }
};

// 新增：计算头像源

const navigateToQuote = (reply: Reply) => {
  clickButton({
    eventType: pageType.POST_DETAIL,
    eventReason: "跳转回复",
    eventValue: JSON.stringify({
      pid: reply.id,
    }),
  });
  router.push({
    path: `/reply/${reply.id}`,
    query: {
      ...route.query,
    },
  });
};
</script>

<style scoped lang="scss">
// 主题色变量
$primary-color: #dd8632;

// 回复区整体样式
.reply-section {
  max-width: 800px;
  margin: 0 auto;
  background: #fff;
  min-height: 220px;
  margin-top: 8px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
  padding-bottom: 100px;

  // 顶部导航栏样式
  .reply-navbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 18px 20px 10px 20px;
    border-bottom: 1px solid #f7f7f7;
  }

  // 标签切换样式
  .reply-tabs {
    display: flex;
    gap: 18px;
  }

  .tab {
    font-size: 14px;
    color: #888;
    font-weight: 400;
    cursor: pointer;

    &.active {
      position: relative;
      transition: all 0.3s ease;
      color: #222;
      font-weight: 700;

      &::after {
        content: "";
        position: absolute;
        bottom: -12px;
        left: 0;
        width: 100%;
        height: 2px;
        background: $primary-color;
        border-radius: 1px;
      }
    }
  }

  // 排序按钮样式
  .reply-sort {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .sort {
    font-size: 12px;
    color: #bbb;
    font-weight: 400;
    cursor: pointer;

    &.active {
      color: $primary-color;
      font-weight: 700;
    }
  }

  .divider {
    color: #ddd;
    font-size: 16px;
  }

  // 回复列表样式
  .reply-list {
    padding: 0 5px;
  }

  .reply-card {
    background: #fff;
    border-radius: 8px;
    margin-bottom: 5px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
    padding: 16px;
  }

  // 回复头部样式
  .reply-header {
    display: flex;
    align-items: flex-start;
    margin-bottom: 8px;
    position: relative;

    .reply-avatar {
      cursor: pointer;
      margin-right: 10px;
    }

    .reply-user-info {
      display: flex;
      flex-direction: column;
    }

    .reply-username-wrapper {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 2px;
    }

    .reply-username {
      font-weight: 600;
    }

    .reply-titles {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      align-items: center;
    }

    .reply-title-badge {
      display: inline-flex;
      align-items: center;
      padding: 4px 10px;
      font-size: 10px;
      font-weight: 500;
      color: #fff;
      background: linear-gradient(135deg, #ffb400 0%, #ff9500 100%);
      border-radius: 12px;
      box-shadow: 0 2px 6px rgba(255, 180, 0, 0.25);
      white-space: nowrap;
      line-height: 1.2;
      transition: all 0.2s ease;
      position: relative;
      overflow: hidden;
    }

    .reply-floor {
      color: #bbb;
      font-size: 12px;
    }

    .reply-actions {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-left: auto;
    }

    .translate-btn {
      font-size: 12px;
      color: #fff;
      background: linear-gradient(135deg, $primary-color, #ffb366);
      cursor: pointer;
      padding: 4px 12px;
      border-radius: 15px;
      transition: all 0.2s ease;

      &:hover {
        opacity: 0.9;
      }
    }

    .more-icon {
      width: 24px;
      height: 24px;
      cursor: pointer;
    }
  }

  // 回复内容样式
  .reply-content {
    margin-bottom: 8px;
    padding-left: 50px;
    word-break: break-word;
    :deep(img) {
      max-width: 100%;
      height: auto;
      cursor: pointer;
    }

    .translated-tip {
      font-size: 12px;
      color: #999;
      margin-bottom: 8px;
      font-style: italic;
    }
  }

  // 引用内容样式
  .reply-quotes {
    background: #f7f7f7;
    border-radius: 6px;
    padding: 8px 12px;
    margin-bottom: 15px;
    margin-left: 45px;

    .reply-quote {
      margin-bottom: 4px;
    }

    .quote-reply {
      color: #999;
      font-size: 12px;
      margin: 0 5px;
      font-weight: 600;
      font-style: italic;
    }

    .quote-username {
      color: $primary-color;
      font-weight: 600;
      font-size: 14px;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }

    .quote-colon {
      color: $primary-color;
      margin-right: 2px;
    }

    .author {
      background: #ffe9a7;
      color: $primary-color;
      font-weight: 600;
      border-radius: 15px;
      font-size: 12px;
      margin-left: 4px;
      padding: 4px 6px;
    }

    .quote-content {
      color: #555;
    }

    .view-more-quotes {
      color: #999;
      font-size: 12px;
      margin-top: 8px;
      cursor: pointer;

      &:hover {
        color: $primary-color;
      }
    }
  }

  // 回复底部样式
  .reply-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-left: 50px;

    .reply-date {
      color: #bbb;
      font-size: 12px;
    }

    .reply-actions {
      display: flex;
      align-items: center;
      gap: 15px;
      color: #717171;
      cursor: pointer;
    }

    .like-item {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
    }

    .icon {
      width: 18px;
      height: 18px;
    }
  }

  .reply-highlight {
    border: 2px solid #dd8632 !important;
  }

  // 加载更多/无更多内容样式，参考Home.vue保持一致
  .load-more-loading,
  .load-more-tip,
  .load-more-end {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px 0;
    color: #999;
    font-size: 14px;
    span {
      margin-left: 8px;
    }
  }

  .load-more-tip {
    color: #666;
    span {
      position: relative;
      &::before {
        content: "";
        position: absolute;
        left: -20px;
        top: 50%;
        transform: translateY(-50%);
        width: 12px;
        height: 12px;
        border: 2px solid #ddd;
        border-top-color: transparent;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
    }
  }

  .load-more-end {
    color: #ccc;
    font-size: 12px;
  }

  @keyframes spin {
    0% {
      transform: translateY(-50%) rotate(0deg);
    }
    100% {
      transform: translateY(-50%) rotate(360deg);
    }
  }
}

// 通用icon样式
.icon {
  width: 20px;
  height: 20px;
  margin-bottom: 1px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  transition: all 0.3s ease;

  &.like-icon {
    background-image: url("@/assets/like.png");

    &.is-active {
      background-image: url("@/assets/like-active.png");
    }
  }
}
</style>
